import React, { useState, useEffect } from "react"
import styles from "../../styles/global.module.scss"

export default function Estimator(props) {
  const { estimator_pricing } = props

  const {
    Price,
    MinimumAreaLength,
    MaximumAreaLength,
    MinimumAreaWidth,
    MaximumAreaWidth,
    MaterialQuality,
    RemoveOldMaterials,
  } = estimator_pricing

  // Set max sq.ft. default if not entered in cms
  let min_sqft = 0,
    max_sqft = 1000

  // Initialize local state
  let [localState, setLocalState] = useState({
    address: "",
    area_length: 0,
    area_width: 0,
    min_area_length: 0,
    max_area_length: 0,
    min_area_width: 0,
    max_area_width: 0,
    email: "",
    material_quality_price: {
      low: 0,
      medium: 0,
      high: 0,
    },
    message: "",
    name: "",
    old_materials: "no",
    phone: "",
    price_per_sq_ft: 0,
    old_materials_price: 0,
    service_date: "",
    service_time: "",
    total: 0,
    quality: "low",
  })

  // Calculate total on input update
  // prettier-ignore
  useEffect(() => {
    setLocalState({
      ...localState,
      total: calculateTotal(),
    }) 
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    localState.area_length,
    localState.area_width,
    localState.quality,
    localState.old_materials,
  ])

  // Set prices on load
  useEffect(() => {
    if (estimator_pricing) {
      setLocalState({
        ...localState,
        price_per_sq_ft: Price,
        min_area_length: MinimumAreaLength,
        max_area_length: MaximumAreaLength,
        min_area_width: MinimumAreaWidth,
        max_area_width: MaximumAreaWidth,
        material_quality_price: {
          low: MaterialQuality.Low,
          medium: MaterialQuality.Medium,
          high: MaterialQuality.High,
        },
        old_materials_price: RemoveOldMaterials,
      })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Update state on form field change
  const onChange = e => {
    e.preventDefault()
    setLocalState({
      ...localState,
      [e.target.name]: e.target.value,
    })
  }

  // Format total as currency
  // https://stackoverflow.com/questions/149055/how-to-format-numbers-as-currency-string
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  })

  // Calculate total estimate
  function calculateTotal() {
    // Get values from local state
    let {
      area_length,
      area_width,
      price_per_sq_ft,
      quality,
      old_materials,
      old_materials_price,
    } = localState

    // Initialize total
    let current_total = 0

    // Get material quality price
    let material_quality_price = parseInt(
      localState.material_quality_price[quality]
    )

    // If area set, calculate total estimate
    if (area_length > 0 || area_width > 0) {
      let area = parseInt(area_length) + parseInt(area_width)

      // Get total price per square ft and add material quality cost
      // prettier-ignore
      current_total = (area * price_per_sq_ft) + (area * material_quality_price)

      // If removing old materials
      if (old_materials === "yes") {
        // prettier-ignore
        current_total = current_total + (area * (old_materials_price * price_per_sq_ft))
      }
    }
    return current_total
  }

  return (
    <form
      id={styles.estimatorForm}
      className={styles.primaryForm}
      name="Estimator"
      method="POST"
      netlify-honeypot="bot-field"
      data-netlify="true"
      action="/thank-you"
    >
      <input type="hidden" name="form-name" value="Estimator" />
      <input type="hidden" name="bot-field" />
      <div className={`${styles.formItem} ${styles.formRangeContain}`}>
        <label htmlFor="area_length">
          Area Length (Max: {localState.max_area_width || max_sqft} Square Ft.)
        </label>
        <div className={`${styles.flexWrapper} ${styles.formRange}`}>
          <input
            class={styles.sqftInput}
            name="area_length"
            min={localState.min_area_length || min_sqft}
            max={localState.max_area_length || max_sqft}
            onBlur={e => onChange(e)}
            onChange={e => onChange(e)}
            step="5"
            type="range"
            value={localState.area_length}
          />
          <div className={styles.sqftTotal}>
            <input
              name="area_length"
              min={localState.min_area_length || min_sqft}
              max={localState.max_area_length || max_sqft}
              onBlur={e => onChange(e)}
              onChange={e => onChange(e)}
              type="number"
              value={localState.area_length}
            />
          </div>
        </div>
      </div>
      <div className={`${styles.formItem} ${styles.formRangeContain}`}>
        <label htmlFor="area_width">
          Area Width (Max: {localState.max_area_width || max_sqft} Square Ft.)
        </label>
        <div className={`${styles.flexWrapper} ${styles.formRange}`}>
          <input
            class={styles.sqftInput}
            name="area_width"
            min={localState.min_area_width || min_sqft}
            max={localState.max_area_width || max_sqft}
            onBlur={e => onChange(e)}
            onChange={e => onChange(e)}
            step="5"
            type="range"
            value={localState.area_width}
          />
          <div className={styles.sqftTotal}>
            <input
              name="area_width"
              min={localState.min_area_width || min_sqft}
              max={localState.max_area_width || max_sqft}
              onBlur={e => onChange(e)}
              onChange={e => onChange(e)}
              type="number"
              value={localState.area_width}
            />
          </div>
        </div>
      </div>
      <div className={styles.flexWrapper}>
        <div className={styles.formItem}>
          <label htmlFor="quality">Material Quality</label>
          <select
            name="quality"
            onBlur={e => onChange(e)}
            onChange={e => onChange(e)}
            value={localState.quality}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
        <div className={styles.formItem}>
          <label htmlFor="old_materials">Remove Old Materials?</label>
          <select
            name="old_materials"
            onBlur={e => onChange(e)}
            onChange={e => onChange(e)}
            value={localState.old_materials}
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>
      </div>
      <div id={styles.estimatorTotal}>
        <p>Total: {formatter.format(localState.total)}</p>
        <input type="hidden" name="total" value={localState.total} />
      </div>
      <div className={styles.formBreak}></div>
      <div className={styles.flexWrapper}>
        <div className={styles.formItem}>
          <label htmlFor="name">Name </label>
          <input
            type="text"
            name="name"
            placeholder="Your Name"
            onBlur={e => onChange(e)}
            onChange={e => onChange(e)}
          />
        </div>
        <div className={styles.formItem}>
          <label htmlFor="email">Email </label>
          <input
            type="email"
            name="email"
            placeholder="Your Email"
            onBlur={e => onChange(e)}
            onChange={e => onChange(e)}
          />
        </div>
      </div>
      <div className={styles.flexWrapper}>
        <div className={styles.formItem}>
          <label htmlFor="phone">Phone </label>
          <input
            type="phone"
            name="phone"
            placeholder="Phone Number"
            onBlur={e => onChange(e)}
            onChange={e => onChange(e)}
          />
        </div>
        <div className={styles.formItem}>
          <label htmlFor="address">Address</label>
          <input
            type="text"
            name="address"
            placeholder="Address"
            onBlur={e => onChange(e)}
            onChange={e => onChange(e)}
          />
        </div>
      </div>
      <div className={styles.formItem}>
        <label htmlFor="message">Message </label>
        <textarea
          name="message"
          placeholder="Have any questions about your estimate?"
          rows="4"
          cols="50"
          onBlur={e => onChange(e)}
          onChange={e => onChange(e)}
        />
      </div>
      <div data-netlify-recaptcha="true"></div>
      <div className={styles.buttonContain}>
        <button
          className={styles.btn}
          type="submit"
          aria-label="Get An Estimate"
        >
          Submit
        </button>
      </div>
    </form>
  )
}
